import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter
} from 'reactstrap';
import Select from 'react-select';
import { EditorState, convertToRaw, convertFromRaw } from 'draft-js';
import ResizableImage from './ResizableImage';
import { draftContentStateToHtml } from '../../util/draftToHtml';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import Swal from 'sweetalert2';
import { customDropdownWithBorder } from '../../util/reactCustomStyles';
import { createPassage, updatePassage, fetchPassages } from '../../redux/QuestionBank/PassageAction';
import { fetchSubjects, fetchUnits } from '../../redux/QuestionBank/SubjectsUnitsAction';
import { passageToolbar, editorContainerStyles } from '../../util/editorToolbarConfig';
import { useUnsavedChangesWarning } from '../../util/useUnsavedChangesWarning';

const PassageBuilder = ({ userEmail, editingPassage = null, onPassageSaved = null }) => {
  const dispatch = useDispatch();

  // Redux state
  const { subjects } = useSelector(state => state.questionBankSubjects);
  const { units } = useSelector(state => state.questionBankUnits);
  const { loading } = useSelector(state => state.questionBankPassages);

  // Editor state
  const [passageTextEditor, setPassageTextEditor] = useState(EditorState.createEmpty());

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    subjectId: '',
    unitId: '',
    tags: []
  });

  const [tagInput, setTagInput] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // Warn on tab close / browser navigation when form has unsaved changes
  useUnsavedChangesWarning(isDirty);

  // Ref to always have latest editor state (avoids stale closures)
  const passageTextEditorRef = useRef(passageTextEditor);
  passageTextEditorRef.current = passageTextEditor;

  const passageResize = useCallback((entityKey, newWidth) => {
    const editorState = passageTextEditorRef.current;
    const contentState = editorState.getCurrentContent();
    const newContentState = contentState.mergeEntityData(entityKey, { width: String(newWidth) });
    const newEditorState = EditorState.push(editorState, newContentState, 'apply-entity');
    setPassageTextEditor(newEditorState);
    setIsDirty(true);
  }, []);

  const passageBlockRenderer = useCallback((block) => {
    if (block.getType() === 'atomic') {
      return {
        component: ResizableImage,
        editable: false,
        props: { onResize: passageResize },
      };
    }
    return undefined;
  }, [passageResize]);

  // Filtered units based on selected subject
  const filteredUnits = formData.subjectId
    ? units.filter(unit => unit.subjectId === formData.subjectId)
    : [];

  useEffect(() => {
    dispatch(fetchSubjects());
    dispatch(fetchUnits());
  }, [dispatch]);

  // Load editing passage data
  useEffect(() => {
    if (editingPassage) {
      // M2: Wrap convertFromRaw in try/catch to prevent crashes from malformed JSON
      try {
        if (editingPassage.passageText) {
          setPassageTextEditor(EditorState.createWithContent(convertFromRaw(editingPassage.passageText)));
        }
      } catch (err) {
        console.error('M2: Failed to parse passage Draft.js content:', err);
        Swal.fire({
          icon: 'warning',
          title: 'Content Load Error',
          text: 'The passage text could not be loaded. The stored content may be corrupted. You can re-enter the text manually.',
          confirmButtonColor: '#5e72e4'
        });
      }

      // Load form data
      setFormData({
        title: editingPassage.title || '',
        subjectId: editingPassage.subjectId || '',
        unitId: editingPassage.unitId || '',
        tags: editingPassage.tags || []
      });
    }
  }, [editingPassage]);

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({
        ...formData,
        tags: [...formData.tags, tagInput.trim()]
      });
      setTagInput('');
      setIsDirty(true);
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter(tag => tag !== tagToRemove)
    });
    setIsDirty(true);
  };

  const handleReset = () => {
    setFormData({
      title: '',
      subjectId: '',
      unitId: '',
      tags: []
    });
    setPassageTextEditor(EditorState.createEmpty());
    setTagInput('');
    setIsDirty(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.title.trim()) {
      Swal.fire('Error', 'Please enter a passage title', 'error');
      return;
    }

    if (!formData.subjectId) {
      Swal.fire('Error', 'Please select a subject', 'error');
      return;
    }

    if (!formData.unitId) {
      Swal.fire('Error', 'Please select a unit/discipline', 'error');
      return;
    }

    const passageContent = passageTextEditor.getCurrentContent();
    if (!passageContent.hasText()) {
      Swal.fire('Error', 'Please enter the passage text', 'error');
      return;
    }

    // Prepare passage data
    const passageData = {
      title: formData.title.trim(),
      subjectId: formData.subjectId,
      unitId: formData.unitId,
      passageText: convertToRaw(passageContent),
      tags: formData.tags
    };

    // M1: Show loading dialog properly via Swal.fire with didOpen
    Swal.fire({
      title: editingPassage ? 'Updating Passage...' : 'Creating Passage...',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading()
    });

    let result;
    if (editingPassage) {
      // Update existing passage
      result = await dispatch(updatePassage(editingPassage.passageId, passageData, userEmail));
    } else {
      // Create new passage
      result = await dispatch(createPassage(passageData, userEmail));
    }

    Swal.close();

    if (result.success) {
      Swal.fire(
        'Success!',
        `Passage ${editingPassage ? 'updated' : 'created'} successfully`,
        'success'
      );

      // Reset form
      handleReset();

      // Refresh passages list
      dispatch(fetchPassages());

      // Call callback if provided
      if (onPassageSaved) {
        onPassageSaved();
      }
    } else {
      Swal.fire('Error', result.error, 'error');
    }
  };

  return (
    <Card className="shadow">
      <CardHeader className="bg-transparent">
        <h3 className="mb-0">{editingPassage ? 'Edit Passage' : 'Create New Passage'}</h3>
        <p className="text-sm text-muted mb-0 mt-2">
          Create reading passages that can be shared across multiple questions
        </p>
      </CardHeader>
      <CardBody>
        <Form onSubmit={handleSubmit}>
          {/* Passage Title */}
          <FormGroup>
            <Label className="form-control-label" style={{ color: '#32325d', fontWeight: '600' }}>
              Passage Title <span className="text-danger">*</span>
            </Label>
            <Input
              type="text"
              placeholder="e.g., Cellular Respiration Process"
              value={formData.title}
              onChange={(e) => { setFormData({ ...formData, title: e.target.value }); setIsDirty(true); }}
              required
            />
            <small className="text-muted">
              Give this passage a descriptive title for easy identification
            </small>
          </FormGroup>

          {/* Subject and Unit Selection */}
          <Row>
            <Col md="6">
              <FormGroup>
                <Label className="form-control-label" style={{ color: '#32325d', fontWeight: '600' }}>
                  Subject <span className="text-danger">*</span>
                </Label>
                <Select
                  options={subjects.map(subject => ({
                    value: subject.subjectId,
                    label: subject.name
                  }))}
                  value={
                    formData.subjectId
                      ? { value: formData.subjectId, label: subjects.find(s => s.subjectId === formData.subjectId)?.name }
                      : null
                  }
                  onChange={(option) => { setFormData({ ...formData, subjectId: option?.value || '', unitId: '' }); setIsDirty(true); }}
                  styles={customDropdownWithBorder}
                  placeholder="Select a subject"
                  isClearable
                />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label className="form-control-label" style={{ color: '#32325d', fontWeight: '600' }}>
                  Unit <span className="text-danger">*</span>
                </Label>
                <Select
                  options={filteredUnits.map(unit => ({
                    value: unit.unitId,
                    label: unit.name
                  }))}
                  value={
                    formData.unitId
                      ? { value: formData.unitId, label: filteredUnits.find(u => u.unitId === formData.unitId)?.name }
                      : null
                  }
                  onChange={(option) => { setFormData({ ...formData, unitId: option?.value || '' }); setIsDirty(true); }}
                  styles={customDropdownWithBorder}
                  placeholder="Select a unit"
                  isDisabled={!formData.subjectId}
                  isClearable
                />
              </FormGroup>
            </Col>
          </Row>

          {/* Passage Text Editor */}
          <FormGroup>
            <Label className="form-control-label" style={{ color: '#32325d', fontWeight: '600' }}>
              Passage Text <span className="text-danger">*</span>
            </Label>
            <div className="border rounded passage-editor-container" style={{ minHeight: '300px', position: 'relative', zIndex: 1 }}>
              <style>{editorContainerStyles}</style>
              <Editor
                editorState={passageTextEditor}
                onEditorStateChange={(state) => { setPassageTextEditor(state); setIsDirty(true); }}
                wrapperClassName="wrapper-class"
                editorClassName="editor-class px-3"
                toolbarClassName="toolbar-class"
                toolbar={passageToolbar}
                customBlockRenderFunc={passageBlockRenderer}
              />
            </div>
            <small className="text-muted">
              Enter the reading passage text. This can include diagrams, tables, or formatted text.
            </small>
          </FormGroup>

          {/* Tags */}
          <FormGroup>
            <Label className="form-control-label" style={{ color: '#32325d', fontWeight: '600' }}>
              Tags (Optional)
            </Label>
            <div className="d-flex">
              <Input
                type="text"
                placeholder="Add a tag"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
              />
              <Button
                color="primary"
                outline
                onClick={handleAddTag}
                className="ml-2"
                type="button"
                style={{ whiteSpace: 'nowrap' }}
              >
                <i className="fas fa-plus mr-2"></i>
                Add
              </Button>
            </div>
            <div className="mt-2">
              {formData.tags.map((tag, index) => (
                <Badge key={index} color="info" className="mr-2 mb-2" style={{ fontSize: '0.875rem', padding: '0.5rem 0.75rem' }}>
                  {tag}
                  <i
                    className="fas fa-times ml-2"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleRemoveTag(tag)}
                  ></i>
                </Badge>
              ))}
            </div>
          </FormGroup>

          {/* Action Buttons */}
          <Row>
            <Col xs="6" md="auto" className="d-md-none mb-2">
              {editingPassage && (
                <Button
                  color="warning"
                  block
                  onClick={() => {
                    handleReset();
                    if (onPassageSaved) {
                      onPassageSaved();
                    }
                  }}
                  type="button"
                >
                  <i className="fas fa-times mr-2"></i>
                  Cancel
                </Button>
              )}
            </Col>
            <Col xs="6" md="auto" className="d-md-none mb-2">
              <Button color="warning" block onClick={handleReset} type="button">
                <i className="fas fa-redo mr-2"></i>
                Reset
              </Button>
            </Col>
            <Col xs="6" md="auto" className="d-md-none mb-2">
              <Button color="info" block onClick={() => setPreviewOpen(true)} type="button">
                <i className="fas fa-eye mr-2"></i>
                Preview
              </Button>
            </Col>
            <Col xs="6" md="auto" className="d-md-none mb-2">
              <Button color="primary" block type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                    {editingPassage ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    <i className={`fas fa-${editingPassage ? 'save' : 'plus'} mr-2`}></i>
                    {editingPassage ? 'Update' : 'Create'}
                  </>
                )}
              </Button>
            </Col>
          </Row>
          <div className="d-none d-md-flex justify-content-end">
            {editingPassage && (
              <Button
                color="warning"
                className="mr-2"
                onClick={() => {
                  handleReset();
                  if (onPassageSaved) {
                    onPassageSaved();
                  }
                }}
                type="button"
              >
                <i className="fas fa-times mr-2"></i>
                Cancel Edit
              </Button>
            )}
            <Button color="warning" className="mr-2" onClick={handleReset} type="button">
              <i className="fas fa-redo mr-2"></i>
              {editingPassage ? 'Clear Form' : 'Reset'}
            </Button>
            <Button color="info" className="mr-2" onClick={() => setPreviewOpen(true)} type="button">
              <i className="fas fa-eye mr-2"></i>
              Preview
            </Button>
            <Button color="primary" type="submit" disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                  {editingPassage ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <i className={`fas fa-${editingPassage ? 'save' : 'plus'} mr-2`}></i>
                  {editingPassage ? 'Update Passage' : 'Create Passage'}
                </>
              )}
            </Button>
          </div>
        </Form>

        {/* Passage Preview Modal */}
        <Modal isOpen={previewOpen} toggle={() => setPreviewOpen(false)} size="xl" scrollable>
          <ModalHeader toggle={() => setPreviewOpen(false)}>
            <i className="fas fa-eye mr-2"></i>
            Passage Preview
          </ModalHeader>
          <ModalBody>
            {/* Metadata Badges */}
            <div className="mb-4 d-flex flex-wrap">
              <Badge color="primary" className="mr-2 mb-1">
                {subjects.find(s => s.subjectId === formData.subjectId)?.name || 'No subject'}
              </Badge>
              <Badge color="success" className="mr-2 mb-1">
                {filteredUnits.find(u => u.unitId === formData.unitId)?.name || 'No unit'}
              </Badge>
            </div>

            {/* Passage Title */}
            <div className="mb-4">
              <h6 className="text-uppercase text-muted font-weight-bold mb-3" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                <i className="fas fa-heading mr-2"></i>Title
              </h6>
              <h4>{formData.title || <span className="text-muted">No title entered</span>}</h4>
            </div>

            {/* Passage Text */}
            <div className="mb-4">
              <h6 className="text-uppercase text-muted font-weight-bold mb-3" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                <i className="fas fa-book-open mr-2"></i>Passage Content
              </h6>
              {(() => {
                try {
                  const contentState = passageTextEditor.getCurrentContent();
                  if (!contentState.hasText()) return <p className="text-muted">No passage text entered</p>;
                  const html = draftContentStateToHtml(contentState);
                  return <div dangerouslySetInnerHTML={{ __html: html }} />;
                } catch (e) {
                  return <p className="text-muted">Unable to render passage text</p>;
                }
              })()}
            </div>

            {/* Tags */}
            {formData.tags.length > 0 && (
              <div className="mb-3">
                <h6 className="text-uppercase text-muted font-weight-bold mb-3" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                  <i className="fas fa-tags mr-2"></i>Tags
                </h6>
                {formData.tags.map((tag, index) => (
                  <Badge key={index} color="info" className="mr-2">
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setPreviewOpen(false)}>
              Close
            </Button>
          </ModalFooter>
        </Modal>
      </CardBody>
    </Card>
  );
};

export default PassageBuilder;
